/**
 * PresentationControls — UPLOAD-AREA часть для category='presentation'.
 *
 * Layout: горизонтальный ряд
 *   ┌────────────────┬─────────────────────────────┐
 *   │ Result slot    │ Upload zone / info          │
 *   │                │ (edit → PPTX drop /         │
 *   │                │  create → подсказка Gamma)  │
 *   └────────────────┴─────────────────────────────┘
 *
 * Режим и модель связаны один-к-одному:
 *   - create → Gamma (gamma-presentation): генерация с нуля по промпту
 *   - edit   → Umnik AI (umnik-slides, под капотом Claude Sonnet):
 *     загрузка PPTX, редактирование текстов
 *
 * Тумблер «Редактировать / Создать с нуля» убран (владелец, 10.09): модель
 * редактирования скрыта в админке, режим руками больше не выбирается — он
 * следует за выбранной моделью (reconcile-effect ниже). Пока umnik-slides
 * скрыта, виджет всегда в режиме create; ветка edit остаётся для истории
 * («Воссоздать» старой правки) и на случай возврата модели.
 *
 * В create-режиме upload-area пустая — все настройки Gamma (Theme, Language,
 * NumCards, ImageSource, Dimensions, TextAmount, Instructions) живут в footer
 * pills `.gm__pills` через PresentationPills → GammaPills. Селектор моделей
 * скрыт для presentation (WidgetModelPicker возвращает null для этой категории).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useGenerate } from '../../../context/GenerateContext'
import { uploadFile } from '../../../api/images'
import PresentationResultSlot from './PresentationResultSlot'
import type { PresentationMode, PresentationSettings } from '../hooks/useGenerateSettings'

const MAX_PPTX_BYTES = 50 * 1024 * 1024
const DEFAULT_PRESENTATION: PresentationSettings = { mode: 'create' }

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} Б`
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
	return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

// backendModel → режим: режим подтягивается под выбранную модель (модель
// приходит извне — /generate с pre-selected моделью, «Воссоздать» из истории).
const BACKEND_TO_MODE: Record<string, PresentationMode> = {
	'umnik-slides': 'edit',
	'gamma-presentation': 'create',
}

interface Props {
	/** Открыть preview (CardModal) — передаётся из GenerateWidget. Клик по
	 *  result-слоту с завершённой генерацией триггерит это. */
	onOpenResult?: () => void
}

export default function PresentationControls({ onOpenResult }: Props = {}) {
	const { settings, updateSetting, selectedModel } = useGenerate()
	const pres = settings.presentation ?? DEFAULT_PRESENTATION

	// Reconcile: режим следует за моделью (не наоборот). Модель приходит извне —
	// Header model selector → /generate с pre-selected моделью, «Воссоздать» из
	// истории; раньше режим оставался дефолтным 'create' и переключал модель
	// обратно на Gamma, игнорируя выбор. Пока umnik-slides скрыта в админке,
	// сюда приходит только Gamma → create.
	useEffect(() => {
		if (!selectedModel || selectedModel.category !== 'presentation') return
		const targetMode = BACKEND_TO_MODE[selectedModel.backendModel || '']
		if (!targetMode || pres.mode === targetMode) return
		updateSetting('presentation', { ...pres, mode: targetMode })
	}, [selectedModel, pres, updateSetting])

	return (
		<div className="pres-controls">
			<div className="pres-controls__row">
				<PresentationResultSlot onOpenPreview={onOpenResult} />
				{pres.mode === 'edit' ? <UmnikUploadZone /> : <GammaCreateHint />}
			</div>
		</div>
	)
}

// ── Gamma create-mode hint ──────────────────────────────────────
// Когда выбран режим Create — upload-area пустая (нет файла для загрузки).
// Показываем подсказку что настройки в pills внизу. Без неё юзер видит
// «зачем тут пустое место».
function GammaCreateHint() {
	return (
		<div className="pres-controls__create-hint">
			<div className="pres-controls__create-hint-icon" aria-hidden="true">
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
					<path
						d="M12 2L14.09 8.26L20 9L15 13.74L16.18 19.74L12 16.77L7.82 19.74L9 13.74L4 9L9.91 8.26L12 2Z"
						stroke="currentColor"
						strokeWidth="1.6"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</div>
			<div className="pres-controls__create-hint-text">
				<div className="pres-controls__create-hint-title">Создание с нуля</div>
				<div className="pres-controls__create-hint-subtitle">
					Опишите презентацию в промпте — Gamma сгенерит. Тему, количество
					слайдов и язык настройте в пилюлях ниже.
				</div>
			</div>
		</div>
	)
}

// ── Claude/Umnik-режим: upload PPTX ────────────────────────────────

function UmnikUploadZone() {
	const { settings, updateSetting, isLoading } = useGenerate()
	const pres = settings.presentation ?? DEFAULT_PRESENTATION

	const setPres = useCallback(
		(patch: Partial<PresentationSettings>) => {
			updateSetting('presentation', { ...pres, ...patch })
		},
		[pres, updateSetting],
	)

	const [uploading, setUploading] = useState(false)
	const [meta, setMeta] = useState<{ name: string; size: number } | null>(null)
	const [uploadError, setUploadError] = useState('')

	const handlePptxUpload = async (file: File) => {
		if (!file.name.toLowerCase().endsWith('.pptx')) {
			setUploadError('Поддерживается только формат PPTX')
			return
		}
		if (file.size > MAX_PPTX_BYTES) {
			setUploadError('Максимальный размер файла — 50 МБ')
			return
		}
		setUploadError('')
		setUploading(true)
		try {
			const res = await uploadFile(file, 'uploads')
			setPres({ fileUrl: res.url })
			setMeta({ name: file.name, size: file.size })
		} catch (e) {
			setUploadError(e instanceof Error ? e.message : 'Ошибка загрузки')
		} finally {
			setUploading(false)
		}
	}

	const removeFile = () => {
		setPres({ fileUrl: undefined })
		setMeta(null)
	}

	return (
		<>
			{pres.fileUrl ? (
				<PptxChip
					name={meta?.name ?? 'Презентация загружена'}
					size={meta?.size}
					subtitle="Готов к редактированию"
					onRemove={removeFile}
					disabled={isLoading || uploading}
				/>
			) : (
				<PptxDropzone
					disabled={isLoading || uploading}
					uploading={uploading}
					title={uploading ? 'Загружаем PPTX...' : 'Загрузи презентацию'}
					hint="Перетащи .pptx сюда · до 50 МБ"
					variant="primary"
					onFile={handlePptxUpload}
				/>
			)}
			{uploadError && <div className="pres-controls__error">{uploadError}</div>}
		</>
	)
}

// ── PptxDropzone (horizontal, compact) ─────────────────────

interface DropzoneProps {
	disabled?: boolean
	uploading?: boolean
	title: string
	hint: string
	variant?: 'primary' | 'secondary'
	onFile: (file: File) => void
}

function PptxDropzone({ disabled, uploading, title, hint, variant = 'primary', onFile }: DropzoneProps) {
	const inputRef = useRef<HTMLInputElement>(null)
	const [hover, setHover] = useState(false)

	const pick = (files: FileList | File[] | null | undefined) => {
		if (!files) return
		const first = Array.from(files)[0]
		if (first) onFile(first)
	}

	return (
		<div
			className={
				'pres-controls__dropzone' +
				` pres-controls__dropzone--${variant}` +
				(hover ? ' pres-controls__dropzone--hover' : '') +
				(disabled ? ' pres-controls__dropzone--disabled' : '')
			}
			onClick={() => !disabled && inputRef.current?.click()}
			onDragOver={(e) => {
				e.preventDefault()
				if (!disabled) setHover(true)
			}}
			onDragLeave={() => setHover(false)}
			onDrop={(e) => {
				e.preventDefault()
				setHover(false)
				if (disabled) return
				pick(e.dataTransfer.files)
			}}
			role="button"
			aria-disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			onKeyDown={(e) => {
				if (disabled) return
				if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
			}}
		>
			<div className="pres-controls__dropzone-icon" aria-hidden="true">
				{uploading ? (
					<svg width="20" height="20" viewBox="0 0 16 16" fill="none" className="spin">
						<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="5 4" />
					</svg>
				) : (
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
						<path
							d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
							stroke="currentColor"
							strokeWidth="1.6"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				)}
			</div>
			<div className="pres-controls__dropzone-text">
				<div className="pres-controls__dropzone-title">{title}</div>
				<div className="pres-controls__dropzone-hint">{hint}</div>
			</div>
			<input
				ref={inputRef}
				type="file"
				accept=".pptx"
				onChange={(e) => {
					pick(e.target.files)
					e.target.value = ''
				}}
				disabled={disabled}
				style={{ display: 'none' }}
			/>
		</div>
	)
}

// ── PptxChip (загруженный файл) ─────────────────────────────

interface ChipProps {
	name: string
	size?: number
	subtitle: string
	onRemove: () => void
	disabled?: boolean
}

function PptxChip({ name, size, subtitle, onRemove, disabled }: ChipProps) {
	return (
		<div className="pres-controls__chip pres-controls__chip--row">
			<span className="pres-controls__chip-icon" aria-hidden="true">📄</span>
			<span className="pres-controls__chip-meta">
				<span className="pres-controls__chip-title">{name}</span>
				<span className="pres-controls__chip-subtitle">
					{size != null ? `${formatBytes(size)} · ${subtitle}` : subtitle}
				</span>
			</span>
			<button
				type="button"
				className="pres-controls__chip-remove"
				onClick={onRemove}
				disabled={disabled}
				aria-label="Убрать файл"
				title="Убрать файл"
			>
				×
			</button>
		</div>
	)
}
